import { iaRa, inRn, mString } from 'lib/helpers/utility';

import { Alert } from 'components/shared/Alert/Alert';
import Badge from 'components/shared/Badge/Badge';
import Divider from 'components/shared/Divider/Divider';
import Typography from 'components/shared/Typography/Typography';

import { useLetter } from '../LetterContext';
import { getCapLabel, isSelfPayPhase } from '../lib/helpers';

const PREVIEW_CAP_ROUTES = new Set(['set-dollar-amount', 'set-higher-allowable-amount']);
const PREVIEW_DEFAULT_BASE_CENTS = 28200;

const titlePrefix = (isSelfPay) =>
  isSelfPay ? 'Estimated cost for your visit' : 'Estimated balance due';

const buildTitle = ({ isSelfPay, balance, activePhase, amounts, oonResponsibility, mode }) => {
  const prefix = titlePrefix(isSelfPay);
  const { capLabel, isCap, capAmount } = getCapLabel(activePhase, amounts, oonResponsibility);

  if (balance != null) {
    const capped =
      isCap && Number.isFinite(capAmount) && capAmount > 0 && balance > capAmount
        ? capAmount
        : balance;
    return `${prefix} ${mString(capped)}`;
  }

  if (mode === 'preview' && activePhase?.charge_limit?.route_to === 'set-higher-allowable-amount') {
    const rawPct = Number(activePhase?.charge_limit?.config?.percent_above ?? 10);
    const pct = Number.isFinite(rawPct) ? rawPct : 10;
    const v = PREVIEW_DEFAULT_BASE_CENTS * (1 + pct / 100);
    return `${prefix} ${mString(v)}`;
  }

  if (isCap || PREVIEW_CAP_ROUTES.has(activePhase?.charge_limit?.route_to)) {
    return `${prefix} ${capLabel}`;
  }

  return `${prefix} $282.00`;
};

const EstimatedBalanceDue = ({ gfeDetails = false }) => {
  const { config, activePhaseField, amounts, totalAmount, patient, appointment, mode } =
    useLetter();
  const insuranceBenefit = iaRa(patient?.userInsuranceBenefit).find(
    (insurance) => insurance.insurance_type === 'primary'
  );

  const patientRating = inRn(insuranceBenefit?.cost_share?.assurance?.score);
  const roundedRating = Math.round(patientRating);
  const ratingValue = inRn(config?.assurance_thresholds?.show_patient_rating);
  const showBadge =
    config?.assurance_thresholds?.show_on_letter &&
    patientRating > 0 &&
    patientRating >= ratingValue;

  const activePhase = iaRa(config?.phases).find((p) => p.field === activePhaseField);
  const isSelfPay = isSelfPayPhase(activePhase);
  const oon = appointment?.oon_patient_responsibility ?? null;
  const hideAmountTitle = mode === 'interactive';

  return (
    <div className="flex flex-col gap-5 px-4 pb-0 pt-5">
      <div className="flex flex-col gap-2">
        {showBadge && (
          <Badge text={`Eligibility assurance: ${roundedRating}%`} variant="light" color="violet" />
        )}
        {!hideAmountTitle && (
          <>
            <Typography variant="h6" weight="medium">
              {buildTitle({
                isSelfPay,
                balance: totalAmount,
                activePhase,
                amounts,
                oonResponsibility: oon,
                mode
              })}
            </Typography>
            <Typography variant="p" size="small">
              {isSelfPay
                ? 'This is an estimated cost for your visit. Final charges may vary based on services rendered.'
                : 'This is an estimated balance due for your visit. Your final cost may change after insurance is processed.'}
            </Typography>
          </>
        )}
        {gfeDetails && (
          <Alert
            title={
              <span className="font-normal">
                The attached invoice includes the expected services and associated costs for this
                visit. Items marked “Separate Scheduling Required” may require a separate Good Faith
                Estimate. You may request one by contacting our practice directly. This Good Faith
                Estimate is provided pursuant to the No Surprises Act (§300gg-136).
              </span>
            }
            message={
              <span className="italic text-neutral-700">
                Changes in insurance status, deductible information, payer processing, or additional
                services provided will impact your final balance. Our practice is committed to
                compliance, clarity, and care.
              </span>
            }
            size="large"
            icon="attach-square-mono"
            variant="lighter"
            className="w-full"
          />
        )}
      </div>
      <Divider />
    </div>
  );
};

export default EstimatedBalanceDue;
